import { useSyncExternalStore } from 'react';

/**
 * 실시간 시계 tick — **모듈 전역 setInterval 1개**를 모든 구독자가 공유한다.
 *
 * 위젯마다 `useEffect(()=>setInterval(...,1000))` 를 두면 시계를 쓰는 위젯 수만큼
 * 타이머가 생기고, 보드에 근태 위젯이 2개(나의/전사)만 얹혀도 초당 렌더가 곱절이 된다.
 * 여기서는 구독자가 1명 이상일 때만 타이머가 돌고, 마지막 구독자가 떠나면 멈춘다.
 *
 * 값은 `Date.now()` 를 1초로 내림한 숫자 — 같은 초 안에서는 참조가 바뀌지 않아
 * useSyncExternalStore 가 불필요한 리렌더를 만들지 않는다.
 */

const listeners = new Set<() => void>();
let timer: ReturnType<typeof setInterval> | null = null;
let snapshot = Math.floor(Date.now() / 1000);

function subscribe(listener: () => void): () => void {
  listeners.add(listener);
  if (timer === null) {
    timer = setInterval(() => {
      const next = Math.floor(Date.now() / 1000);
      if (next === snapshot) return;
      snapshot = next;
      for (const l of listeners) l();
    }, 1000);
  }
  return () => {
    listeners.delete(listener);
    if (listeners.size === 0 && timer !== null) {
      clearInterval(timer);
      timer = null;
    }
  };
}

function getSnapshot(): number {
  return snapshot;
}

/** 1초마다 갱신되는 epoch 초. 시계 표시용. */
export function useClockTick(): number {
  return useSyncExternalStore(subscribe, getSnapshot, getSnapshot);
}

/** HH:MM:SS. */
export function formatClock(epochSeconds: number): string {
  const d = new Date(epochSeconds * 1000);
  return [d.getHours(), d.getMinutes(), d.getSeconds()]
    .map((n) => String(n).padStart(2, '0'))
    .join(':');
}
